import {
  toOloWebhookCredentialSaveError,
  useUpdateOloWebhookCredentialMutation,
} from 'api/oloWebhookCredential'
import { useOloWebhookStatus } from 'hooks/useOloWebhookStatus'
import { buildTranslate } from 'locales'
import { useCallback, useEffect, useState } from 'react'
import type { OloWebhookConnectionState } from 'utilities/oloWebhookStatus'

const t = buildTranslate('live_updates.olo_webhook_modal')

export type UseOloWebhookConnection = {
  connectionState: OloWebhookConnectionState | undefined
  /** ISO timestamp of the most recent Olo webhook across every receipt row, or
   * null when there has never been one (or the queries haven't settled). */
  lastReceivedAt: string | null
  /** Connected, but nothing has arrived in over a day — `connectionState`
   * stays 'connected', but the page banner swaps to its "needs attention"
   * copy/icon/colors and CTA. */
  needsReconnect: boolean
  webhookUrl: string
  isModalOpen: boolean
  openModal: () => void
  closeModal: () => void
  saveSecret: (sharedSecret: string) => Promise<void>
  isSaving: boolean
  saveError: string | null
}

// Merchant-wide Olo webhook connection status (shared useOloWebhookStatus,
// whose queries are skipped when `enabled` is false — the selected
// configuration's ordering provider isn't Olo) + the "Connect Olo webhooks"
// modal's open state and save mutation, which only LiveUpdates needs.
export function useOloWebhookConnection(
  enabled: boolean
): UseOloWebhookConnection {
  const { connectionState, lastReceivedAt, needsReconnect, webhookUrl } =
    useOloWebhookStatus(enabled)
  const [updateCredential, { isLoading: isSaving }] =
    useUpdateOloWebhookCredentialMutation()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // This hook instance lives for the whole LiveUpdates page (only the status
  // queries are skipped when disabled) — without this, switching away from an
  // Olo configuration while the modal is open leaves isModalOpen stuck true,
  // and switching back to any Olo configuration later silently reopens the
  // dialog with a possibly-stale saveError, with no user action.
  useEffect(() => {
    if (!enabled) {
      setIsModalOpen(false)
      setSaveError(null)
    }
  }, [enabled])

  const openModal = useCallback(() => {
    setSaveError(null)
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const saveSecret = useCallback(
    async (sharedSecret: string) => {
      setSaveError(null)
      try {
        await updateCredential(sharedSecret).unwrap()
        // Deliberately leaves the dialog open: it swaps to its own "Key saved"
        // confirmation (Figma 1298:31799), which the merchant dismisses.
      } catch (error) {
        setSaveError(toOloWebhookCredentialSaveError(error) ?? t('save_error'))
        throw error
      }
    },
    [updateCredential]
  )

  return {
    connectionState,
    lastReceivedAt,
    needsReconnect,
    webhookUrl,
    isModalOpen,
    openModal,
    closeModal,
    saveSecret,
    isSaving,
    saveError,
  }
}
